import React from "react";
import ReactMarkdown from "react-markdown";
import { extractText, slugify } from "./markdown";

// Consistent Markdown rendering for Help Center content
// Applies heading IDs, and unified typography for both admin preview and article page.

const MarkdownRenderer = ({ content, variant = "dark", className = "" }) => {
  const baseText = variant === "dark" ? "text-indigo-100/90" : "text-slate-700";

  const components = {
    h1: ({ node, children, ...props }) => (
      <h1
        {...props}
        id={slugify(extractText(node))}
        className={`${variant === "dark" ? "text-white" : "text-slate-900"} mt-2 scroll-mt-28 text-3xl font-semibold tracking-tight`}
      >
        {children}
      </h1>
    ),
    h2: ({ node, children, ...props }) => (
      <h2
        {...props}
        id={slugify(extractText(node))}
        className={`${variant === "dark" ? "text-white" : "text-slate-900"} mt-10 scroll-mt-28 border-t ${
          variant === "dark" ? "border-white/10" : "border-slate-200"
        } pt-6 text-2xl font-semibold`}
      >
        {children}
      </h2>
    ),
    h3: ({ node, children, ...props }) => (
      <h3
        {...props}
        id={slugify(extractText(node))}
        className={`${variant === "dark" ? "text-indigo-100" : "text-slate-800"} mt-6 scroll-mt-28 text-xl font-semibold`}
      >
        {children}
      </h3>
    ),
    p: ({ children, ...props }) => (
      <p {...props} className={`mt-4 leading-7 ${baseText}`}>
        {children}
      </p>
    ),
    a: ({ children, ...props }) => (
      <a
        {...props}
        className={`${
          variant === "dark" ? "text-indigo-300 hover:text-white" : "text-indigo-600 hover:text-indigo-700"
        } underline`}
      >
        {children}
      </a>
    ),
    ul: ({ children, ...props }) => (
      <ul {...props} className="mt-4 list-disc space-y-2 pl-6">
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol {...props} className="mt-4 list-decimal space-y-2 pl-6">
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li {...props} className={`leading-7 ${baseText}`}>
        {children}
      </li>
    ),
    blockquote: ({ children, ...props }) => (
      <blockquote
        {...props}
        className={`mt-6 border-l-4 pl-4 ${
          variant === "dark" ? "border-indigo-400/40 text-indigo-100/80" : "border-indigo-300/60 text-slate-700"
        }`}
      >
        {children}
      </blockquote>
    ),
    code: ({ inline, className, children, ...props }) => (
      <code
        {...props}
        className={`rounded px-1.5 py-0.5 text-[0.85em] ${
          variant === "dark" ? "bg-white/10 text-indigo-100" : "bg-slate-100 text-slate-800"
        } ${className || ""}`}
      >
        {children}
      </code>
    ),
    pre: ({ children, ...props }) => (
      <pre
        {...props}
        className={`mt-6 overflow-x-auto rounded-lg border p-4 text-sm ${
          variant === "dark"
            ? "border-white/10 bg-slate-900/60 text-indigo-100"
            : "border-slate-200 bg-slate-50 text-slate-800"
        }`}
      >
        {children}
      </pre>
    ),
  };

  return (
    <div className={className}>
      <ReactMarkdown components={components}>{content || ""}</ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
